import { JarvisOrb } from "../orb";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SelfKnowledgeCard } from "../components/SelfKnowledgeCards";
import { MessageMarkdown } from "./MessageMarkdown";
import { extractPayloadFromErrorString } from "../selfKnowledgePayloads";
import { useSimoChat, modelOptionsForAssistant, approvalInputPreview } from "./useSimoChat";
import { voiceSelectionStatus } from "../voice/voiceSelectionStatus";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import {
  classicChatEmptyState,
  classicChatMissionPlaceholder,
  classicChatVoiceDeviceDetail,
} from "./classicChatOwnerCopy";

// Klassische einspaltige Chat-Ansicht. Die gesamte Logik liegt im geteilten
// Hook `useSimoChat` (auch vom DashboardView genutzt); diese Komponente rendert
// nur das ursprüngliche Chat-Layout unverändert weiter.
export function ChatView({ assistantName = "SIMO" }: { assistantName?: string } = {}): JSX.Element {
  const {
    orb, input, setInput, error, history, convos,
    activeConvoId, setActiveConvoId, confirmDeleteConvoId, setConfirmDeleteConvoId, busyDeleteConvo,
    mode, setMode, voiceMode, setVoiceMode, model, setModel, roleId, setRoleId, roles, ttsVoice, setTtsVoice,
    voiceOptions, elevenConfigured, voice, canSelectMic, selectedMic, voiceOwnerUx, ttsTestStatus, scrollRef,
    sendText, stopAll, testTtsVoice, newConversation, deleteConvo, performDeleteConvo,
    handleMicDown, handleMicUp,
  } = useSimoChat({ assistantName });
  const modelOptions = modelOptionsForAssistant(assistantName);
  const selectedVoice = voiceOptions.find((option) => option.id === ttsVoice);
  const voiceStatus = voiceSelectionStatus({ selected: selectedVoice, elevenConfigured });

  return (
    <div className="h-full min-h-0 min-w-0 grid grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[240px_1fr] md:grid-rows-1">
      <div className="simo-keyboard-safe-editor md:hidden min-w-0 border-b border-jarvis-border bg-jarvis-surface/30 p-3 flex gap-2 items-center" aria-label={`${assistantName} Mobile Chat Keyboard-safe Conversation Controls`}>
        <select
          id={`${assistantName.toLowerCase()}-conversation-mobile`}
          name={`${assistantName.toLowerCase()}-conversation-mobile`}
          className="input simo-keyboard-safe-field min-h-10 flex-1 min-w-0 text-base sm:text-xs"
          value={activeConvoId ?? ""}
          onChange={(e) => setActiveConvoId(e.target.value || null)}
          title="Konversation wählen"
        >
          <option value="">Aktueller Chat</option>
          {convos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <button className="btn-primary text-xs shrink-0" onClick={() => void newConversation()}>
          + Neu
        </button>
      </div>
      {/* Conversations-Sidebar */}
      <aside className="hidden md:flex border-r border-jarvis-border bg-jarvis-surface/30 flex-col overflow-hidden">
        <div className="p-3 border-b border-jarvis-border">
          <button className="btn-primary w-full text-xs" onClick={() => void newConversation()}>
            + Neue Konversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convos.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-xs border-b border-jarvis-border/40 hover:bg-jarvis-bg/40 ${
                activeConvoId === c.id ? "bg-jarvis-accent/10 border-l-2 border-l-jarvis-accent" : ""
              }`}
              onClick={() => setActiveConvoId(c.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium text-jarvis-text">{c.title}</div>
                <div className="text-[10px] text-jarvis-muted">
                  {new Date(c.updatedAt).toLocaleString("de-DE")}
                </div>
              </div>
              <button
                className="opacity-0 group-hover:opacity-100 text-rose-300 px-1"
                onClick={(e) => {
                  e.stopPropagation();
                  void deleteConvo(c.id);
                }}
                title="Löschen"
              >
                ×
              </button>
            </div>
          ))}
          {convos.length === 0 && (
            <div className="p-4 text-xs text-jarvis-muted">Noch keine Chats.</div>
          )}
        </div>
      </aside>

      {/* Hauptbereich */}
      <div className="min-h-0 min-w-0 flex flex-col overflow-hidden">
        <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 px-4 md:px-6 pt-4">
          <div className="simo-keyboard-safe-editor flex min-w-0 flex-wrap gap-2 items-center" aria-label={`${assistantName} Chat Keyboard-safe Model and Voice Controls`}>
            <select
              id={`${assistantName.toLowerCase()}-model`}
              name={`${assistantName.toLowerCase()}-model`}
              className="input simo-keyboard-safe-field min-h-10 text-base sm:text-xs"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              title="Modell für nächste Anfrage"
            >
              {modelOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              id={`${assistantName.toLowerCase()}-role`}
              name={`${assistantName.toLowerCase()}-role`}
              className="input simo-keyboard-safe-field min-h-10 text-base sm:text-xs"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              title="Agentenrolle für nächste Anfrage"
            >
              <option value="">Rolle: Auto</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}{role.permissionFloor !== "none" ? ` · ${role.permissionFloor}` : ""}
                </option>
              ))}
            </select>
            <select
              id={`${assistantName.toLowerCase()}-tts-voice`}
              name={`${assistantName.toLowerCase()}-tts-voice`}
              className="input simo-keyboard-safe-field min-h-10 text-base sm:text-xs"
              value={ttsVoice}
              onChange={(e) => setTtsVoice(e.target.value as typeof ttsVoice)}
              title={voiceStatus.detail}
            >
              {voiceOptions.map((v) => (
                <option
                  key={v.id}
                  value={v.id}
                >
                  {v.label}
                  {v.fallback ? " — Fallback" : ""}
                  {v.available === false ? ` — ${v.actionLabel ?? "nicht verfügbar"}` : ""}
                </option>
              ))}
            </select>
            <button
              className="btn text-xs"
              onClick={() => void testTtsVoice()}
              disabled={voice.speaking}
              title={voiceStatus.detail}
            >
              {voiceStatus.testLabel}
            </button>
            <span className="card px-2 py-1 text-[10px] text-jarvis-muted" title={`${voiceStatus.label} · ${voiceStatus.detail}`}>
              <strong className="text-jarvis-text">{voiceStatus.action}</strong> · {voiceStatus.testLabel}
            </span>
            <span className="card px-2 py-1 text-[10px] text-jarvis-muted" title={voiceStatus.reason}>
              Gewählt: <strong className="text-jarvis-text">{voiceStatus.selectedLabel}</strong> · Test spielt:{" "}
              <strong className="text-jarvis-text">{voiceStatus.playbackLabel}</strong>
            </span>
            <span className="card px-2 py-1 text-[10px] text-jarvis-muted" data-state={ttsTestStatus.state} title={ttsTestStatus.detail}>
              <strong className="text-jarvis-text">{ttsTestStatus.label}</strong> · {ttsTestStatus.detail}
            </span>
            <select
              id={`${assistantName.toLowerCase()}-microphone`}
              name={`${assistantName.toLowerCase()}-microphone`}
              className="input simo-keyboard-safe-field min-h-10 max-w-[260px] text-base sm:text-xs"
              value={canSelectMic ? voice.selectedDeviceId : ""}
              onChange={(e) => voice.setSelectedDeviceId(e.target.value)}
              onFocus={() => void voice.refreshDevices()}
              disabled={!canSelectMic}
              title="Mikrofon für Push-to-Talk"
            >
              {!canSelectMic ? (
                <option value="">Mic prüfen zum Anzeigen der Gerätenamen</option>
              ) : voice.devices.length === 0 ? (
                <option value="">Kein Mikrofon erkannt</option>
              ) : (
                voice.devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="flex min-w-0 flex-wrap gap-2">
            <button className="btn text-xs" onClick={stopAll}>
              Stop
            </button>
            <button
              className={`btn text-xs ${voiceMode === "server" ? "border-jarvis-accent text-jarvis-accent" : ""}`}
              onClick={() => {
                if (voiceMode === "server") {
                  stopAll();
                  setVoiceMode("off");
                } else {
                  setVoiceMode("server");
                }
              }}
            >
              {voiceMode === "server" ? "Voice an" : "Voice aus"}
            </button>
            <button
              className="btn text-xs"
              onClick={() => setMode(mode === "orb" ? "compact" : "orb")}
            >
              {mode === "orb" ? "Compact" : "Orb"}
            </button>
          </div>
        </div>

        {mode === "orb" && (
          <div className="flex items-center justify-center py-4 shrink-0">
            <JarvisOrb state={orb} size={220} assistantName={assistantName} />
          </div>
        )}

        {/* Message-Liste */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 pb-4 space-y-3">
          {history.length === 0 && (
            <div className="text-center text-jarvis-muted text-sm mt-12">
              {classicChatEmptyState(assistantName)}
            </div>
          )}
          {history.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[min(100%,48rem)] md:max-w-[80%] rounded-xl px-4 py-2.5 text-sm break-words ${
                  m.role === "user"
                    ? "whitespace-pre-wrap bg-jarvis-accent/20 border border-jarvis-accent/40"
                    : "bg-jarvis-surface border border-jarvis-border"
                }`}
              >
                {m.pending ? (
                  <span className="text-jarvis-muted">{assistantName} denkt…</span>
                ) : m.role === "assistant" ? (
                  <MessageMarkdown content={m.content} />
                ) : (
                  m.content
                )}
                {m.draft && (
                  <div className="mt-2 pt-2 border-t border-jarvis-border/40 flex gap-2 items-center">
                    <a
                      href={m.draft.webLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary text-xs"
                    >
                      Öffnen
                    </a>
                    <span className="text-[10px] text-jarvis-muted">
                      Bei Voice an: sag „ja" zum Öffnen, „stopp" zum Verwerfen.
                    </span>
                  </div>
                )}
                {m.payloads && m.payloads.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-jarvis-border/40 space-y-2">
                    {m.payloads.map((p, i) => (
                      <SelfKnowledgeCard key={i} payload={p} />
                    ))}
                  </div>
                )}
                {m.pendingApprovals && m.pendingApprovals.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-jarvis-border/40 space-y-2">
                      {m.pendingApprovals.map((a) => {
                        const preview = approvalInputPreview(a.payloadPreviewJson);
                        return (
                        <div key={a.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs font-medium text-jarvis-text break-words min-w-0">{a.summary}</div>
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-jarvis-bg/60 text-jarvis-muted">
                              {a.riskLevel}
                            </span>
                          </div>
                          <div className="text-[10px] text-jarvis-muted mt-0.5">{a.toolId}</div>
                          {preview && (
                            <pre className="mt-1.5 text-[10px] text-jarvis-muted/90 whitespace-pre-wrap break-words bg-jarvis-bg/40 rounded p-1.5 max-h-32 overflow-auto">
                              {preview}
                            </pre>
                            )}
                            <div className="mt-2 flex flex-wrap gap-2 items-center">
                              <span className="text-[11px] text-amber-200">Legacy-Aktionspfad blockiert</span>
                              <span className="text-[10px] text-jarvis-muted">Freigabe im Approval-Center (Mission-Ansicht) erteilen.</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {m.meta && (
                  <details className="mt-2 pt-2 border-t border-jarvis-border/40 text-[10px] text-jarvis-muted">
                    <summary className="cursor-pointer select-none">
                      Details anzeigen
                    </summary>
                    <div className="mt-2 space-y-1">
                      <div>
                        {m.meta.toolCalls} Tool-Call{m.meta.toolCalls === 1 ? "" : "s"} · {m.meta.iterations} Iter
                        {m.meta.model ? ` · ${m.meta.model}` : ""}
                      </div>
                      {m.meta.assistantMode && <div>Modus: {m.meta.assistantMode}</div>}
                      {m.meta.sanityStatus && <div>Sanity: {m.meta.sanityStatus}</div>}
                      {m.meta.activeToolGroups && m.meta.activeToolGroups.length > 0 && (
                        <div>Aktiv: {m.meta.activeToolGroups.join(", ")}</div>
                      )}
                      {m.meta.blockedToolGroups && m.meta.blockedToolGroups.length > 0 && (
                        <div>Im Hintergrund: {m.meta.blockedToolGroups.join(", ")}</div>
                      )}
                      {m.meta.usedToolNames && m.meta.usedToolNames.length > 0 && (
                        <div>Genutzt: {m.meta.usedToolNames.join(", ")}</div>
                      )}
                      {m.meta.latencies && (
                        <div>
                          Laufzeit: {m.meta.latencies.totalMs} ms · LLM {m.meta.latencies.llmMs} ms · Tools{" "}
                          {m.meta.latencies.toolMs} ms
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Eingabe */}
        <div className="px-4 md:px-6 pb-6 space-y-3 border-t border-jarvis-border/40 pt-4">
          {error &&
            (() => {
              // F-IA-15/17: strukturierte Fehler-Payloads als Karte rendern statt
              // rohem "403: {json}"-Text; sonst Fallback auf den Klartext.
              const ownerError = ownerFacingGlobalError(error);
              const payload = extractPayloadFromErrorString(ownerError);
              return payload ? (
                <SelfKnowledgeCard payload={payload} />
              ) : (
                <div className="card p-3 text-sm border-rose-500/50 text-rose-300">{ownerError}</div>
              );
            })()}
          {voice.error && (
            <div className="card p-3 text-sm border-rose-500/50 text-rose-300">
              Mikrofon: {ownerFacingGlobalError(voice.error)}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-center text-xs text-jarvis-muted">
            <div className="card px-3 py-2 min-w-0">
              <div className="text-jarvis-text font-medium">Voice Mission</div>
              <div className="truncate" title={`${voiceOwnerUx.pttLabel} · ${voiceOwnerUx.ttsLabel}`}>
                {voiceOwnerUx.pttLabel} · {voiceOwnerUx.ttsLabel}
              </div>
              <div className="mt-1 text-[10px] text-jarvis-muted/80">
                {voiceOwnerUx.wakeLabel} · {classicChatVoiceDeviceDetail({
                  canSelectMic,
                  devices: voice.devices.length,
                  selectedDeviceLabel: selectedMic?.label,
                })}
              </div>
            </div>
            <button
              type="button"
              className="btn text-xs"
              onClick={() => void voice.requestPermission()}
            >
              Mic prüfen
            </button>
          </div>
          <div className="simo-keyboard-safe-editor flex min-w-0 flex-col sm:flex-row gap-2" aria-label={`${assistantName} Chat Keyboard-safe Mission Composer`}>
            <button
              type="button"
              className={`btn min-w-0 shrink-0 sm:min-w-[180px] ${
                voice.recording ? "bg-rose-500/20 text-rose-200 border-rose-500" : ""
              }`}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                handleMicDown();
              }}
              onPointerUp={(e) => {
                e.currentTarget.releasePointerCapture(e.pointerId);
                handleMicUp();
              }}
              onPointerCancel={() => voice.cancelRecording()}
              disabled={!voice.available || voiceMode === "off"}
              title="Halten zum Sprechen. Leertaste funktioniert auch, wenn das Textfeld leer ist."
            >
              {voice.recording ? "Aufnahme laeuft..." : "Halten zum Sprechen"}
            </button>
            <input
              id={`${assistantName.toLowerCase()}-prompt`}
              name={`${assistantName.toLowerCase()}-prompt`}
              className="input simo-keyboard-safe-field min-h-10 flex-1 text-base sm:text-sm"
              placeholder={classicChatMissionPlaceholder(assistantName)}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void sendText(input);
                }
              }}
            />
            <button className="btn-primary" onClick={() => void sendText(input)}>
              Mission senden
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDeleteConvoId}
        onClose={() => setConfirmDeleteConvoId(null)}
        title="Konversation löschen?"
        confirmLabel="Endgültig löschen"
        variant="danger"
        busy={busyDeleteConvo}
        busyLabel="Lösche…"
        onConfirm={() => void performDeleteConvo()}
      >
        <p className="text-sm text-jarvis-muted">
          Endgültig, nicht rückgängig zu machen. Der Verlauf wird komplett entfernt.
        </p>
      </ConfirmDialog>
    </div>
  );
}
